import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom'
import AttendanceForm from './components/AttendanceForm'
import ClassesPage from './pages/ClassesPages'
import ClassDetailPage from './pages/ClassDetailPage'
import MyTasksPage from './pages/MyTasksPage'
import AttendancePage from './pages/AttendancePage'
import StudentDetailPage from './pages/StudentDetailPage'
import TeacherDetailPage from './pages/TeacherDetailPage'
import ParentDetailPage from './pages/ParentDetailPage'
import StudentNotesPage from './pages/StudentNotesPage'
import Header from './components/Header'
import SideMenu from './components/SideMenu'
import CssBaseline from '@mui/material/CssBaseline'
import { styled, useTheme } from '@mui/material/styles'
import { useMediaQuery } from '@mui/material'
import './styles/theme.css' // Import our CSS variables
import MyStatsPage from './pages/MyStatsPage'
import { ThemeProvider } from './theme/ThemeContext'
import SMSPage from './components/SMS/SMSPage'
// import MessagesPage from './pages/MessagesPage'
// import NotificationPreferencesPage from './pages/NotificationPreferencesPage'
import { AuthProvider, useAuth } from './context/AuthContext'
import ProtectedRoute from './components/auth/ProtectedRoute'
import PublicRoute from './components/auth/PublicRoute'
import LoginForm from './components/auth/LoginForm'
import RegisterForm from './components/auth/RegisterForm'
import AdminDashboard from './pages/AdminDashboard'
import ForgotPasswordForm from './components/auth/ForgotPasswordForm'
import ResetPasswordForm from './components/auth/ResetPasswordForm'
import AdminManagementPage from './pages/AdminManagementPage'
import AdminClassesPage from './pages/AdminClassesPage'
import AdminManageClassPage from './pages/AdminManageClassPage'
import PendingUsersPage from './pages/PendingUsersPage'
import TeacherActivityPage from './pages/TeacherActivityPage'
import SubstituteManagementPage from './pages/SubstituteManagementPage'
import SubstituteAssignmentsPage from './pages/SubstituteAssignmentsPage'
import DataExportPage from './pages/DataExportPage'
import ActivePage from './pages/ActivePage'
import ArchivePage from './pages/ArchivePage'
import WaitlistPage from './pages/WaitlistPage'
import SmsOptInPolicy from './components/SMS/SmsOptInPolicy'
import PrivacyPolicy from './components/Legal/PrivacyPolicy'
import EmailConfirmationPage from './pages/EmailConfirmationPage'
import SettingsPage from './pages/SettingsPage'
// import OptInConfirmation from './components/SMS/OptInConfirmation'

// App container
const AppContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
}))

// Main content area
const MainContent = styled('main')(({ theme, isSidebarOpen, isAuthenticated, hideHeader }) => ({
  flex: 1,
  padding: hideHeader ? '0px' : theme.spacing(2),
  marginTop: hideHeader ? '0px' : '72px', // Match header height or 0 if no header
  marginLeft: isAuthenticated ? (isSidebarOpen ? '240px' : '60px') : '0px', // Adjust based on sidebar state and auth
  transition: theme.transitions.create(['margin-left'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  [theme.breakpoints.down('md')]: {
    marginLeft: '0px', // Always no margin on mobile (drawer is overlay)
  },
}))

function AppContent() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isSidebarOpen, setSidebarOpen] = useState(() => {
    // On mobile, default to closed; on desktop, check localStorage
    if (window.innerWidth < theme.breakpoints.values.md) {
      return false;
    }
    const saved = localStorage.getItem('sideMenuOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const { user, userRole } = useAuth();
  const location = useLocation();
  
  // Check if current route should hide header
  const hideHeader = location.pathname === '/login' || 
                      location.pathname === '/register' || 
                      location.pathname === '/forgot-password' || 
                      location.pathname === '/reset-password' ||
                      location.pathname === '/sms-policy' ||
                      location.pathname === '/privacy-policy' ||
                      location.pathname === '/email-confirmed';
  
  // Save sidebar state to localStorage whenever it changes (only on desktop)
  useEffect(() => {
    if (!isMobile) {
    localStorage.setItem('sideMenuOpen', JSON.stringify(isSidebarOpen));
    }
  }, [isSidebarOpen, isMobile]);

  // Close sidebar when switching to mobile view
  useEffect(() => {
    if (isMobile && isSidebarOpen) {
      setSidebarOpen(false);
    }
  }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  return (
    <AppContainer>
      {!hideHeader && <Header toggleDrawer={toggleSidebar} />}
      {user && <SideMenu isOpen={isSidebarOpen} toggleDrawer={toggleSidebar} />}
      <MainContent 
        isSidebarOpen={isSidebarOpen} 
        isAuthenticated={!!user} 
        hideHeader={hideHeader}
      >
          <Routes>
            {/* Public routes */}
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <LoginForm />
                </PublicRoute>
              } 
            />
            <Route 
              path="/register" 
              element={
                <PublicRoute>
                  <RegisterForm />
                </PublicRoute>
              } 
            />
            <Route 
              path="/sms-policy" 
              element={<SmsOptInPolicy />} 
            />
            <Route 
              path="/privacy-policy" 
              element={<PrivacyPolicy />} 
            />
            <Route 
              path="/email-confirmed" 
              element={<EmailConfirmationPage />} 
            />

            {/* Admin routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sms"
              element={
                <ProtectedRoute adminOnly={true}>
                  <SMSPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/classes"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminClassesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/class/:classId/manage"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminManageClassPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pending"
              element={
                <ProtectedRoute adminOnly={true}>
                  <PendingUsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/teacher-activity"
              element={
                <ProtectedRoute adminOnly={true}>
                  <TeacherActivityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/substitute-management"
              element={
                <ProtectedRoute adminOnly={true}>
                  <SubstituteManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/export"
              element={
                <ProtectedRoute adminOnly={true}>
                  <DataExportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/active"
              element={
                <ProtectedRoute adminOnly={true}>
                  <ActivePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/archive"
              element={
                <ProtectedRoute adminOnly={true}>
                  <ArchivePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/waitlist"
              element={
                <ProtectedRoute adminOnly={true}>
                  <WaitlistPage />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Navigate to={userRole === 'admin' ? '/admin' : `/teacher/${user?.id}/dashboard`} replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/student/:studentId/class/:classId"
              element={
                <ProtectedRoute>
                  <AttendanceForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/student_details/:studentId"
              element={
                <ProtectedRoute>
                  <StudentDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/student-notes/:studentId?"
              element={
                <ProtectedRoute>
                  <StudentNotesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/:teacherId/classes"
              element={
                <ProtectedRoute>
                  <ClassesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/:teacherId/tasks"
              element={
                <ProtectedRoute>
                  <MyTasksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/:teacherId/dashboard"
              element={
                <ProtectedRoute>
                  <MyStatsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/:teacherId/substitute-assignments"
              element={
                <ProtectedRoute>
                  <SubstituteAssignmentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/:teacherId/attendance"
              element={
                <ProtectedRoute>
                  <AttendancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher_details/:teacherId"
              element={
                <ProtectedRoute>
                  <TeacherDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent_details/:parentId"
              element={
                <ProtectedRoute>
                  <ParentDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/class/:classId"
              element={
                <ProtectedRoute>
                  <ClassDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            {/* Commented out messaging routes
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/:teacherId/messages"
              element={
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notification-preferences"
              element={
                <ProtectedRoute>
                  <NotificationPreferencesPage />
                </ProtectedRoute>
              }
            />
            */}
            <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordForm /></PublicRoute>} />
            <Route path="/reset-password" element={<PublicRoute><ResetPasswordForm /></PublicRoute>} />
            <Route
              path="/admin/management"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminManagementPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </MainContent>
      </AppContainer>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <CssBaseline />
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
